import { useQueryClient } from '@tanstack/react-query'
import FbButton from 'components/FbUI/FbButton'
import { FBPage } from 'components/FbUI/Page/FBPage'
import ImportContactModal from 'components/ImportContactFlow/ImportContactModal'
import { ContactRequestsTable } from 'components/Tables/ContactRequestsTable/ContactRequestsTable'
import { ContactsTable } from 'components/Tables/ContactsTable/ContactsTable'
import useDocumentTitle from 'components/useDocumentTitle'
import React from 'react'
import { PiAddressBook, PiPlus, PiUploadSimple } from 'react-icons/pi'
import { UnderlineTabs } from '../../../components/FbUI/UnderlineTabs'
import NewContactModal from '../../../components/Modals/NewContactModal'
import {
  CONTACT_REQUEST_JOB_TABLE_KEY,
  CONTACTS_TABLE_KEY,
} from '../../../constants/tableQueryKeys'
import { useNavigate, useParams } from 'react-router-dom'
import { useNavbarStore } from '../../../components/Layout/navbar-store'
import { useFeatureFlagService } from 'utils/useFeatureFlagService'
import { PATHS } from 'paths'
import {
  FBPageTableContainer,
  FBPageTitle,
  FBPageTitleRow,
} from '../../../components/FbUI/Page/styles'
import {
  BuyCreditsButton,
  CreditBalanceCard,
} from 'components/Credits/CreditBalanceCard'
import { toast } from 'react-toastify'
import { TableLinkStyleProvider } from 'components/Tables/CommonTableCells/TableLinkStyleContext'
import { ToolbarDensityProvider } from 'components/Tables/CommonTableCells/ToolbarDensityContext'

export function ContactsPage() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const params = useParams()
  const selectedTab = (params?.type ?? 'all') as 'all' | 'requests'
  const store = useNavbarStore()

  const [totalContacts, setTotalContacts] = React.useState<number | null>(null)
  const [showNewContactModal, setShowNewContactModal] = React.useState(false)
  const [showImportContactModal, setShowImportContactModal] =
    React.useState(false)
  // Lazy-mount-once: each tab mounts the first time it's visited and stays
  // mounted thereafter (hidden via display:none when inactive). Avoids the
  // loading→empty→filled flicker on every subsequent tab switch. Use a ref
  // so external URL changes (back/forward, deep link) — which bypass the
  // PillTabs onChange — still grow the set; the ref also doesn't trigger
  // re-renders on tab change since the panel visibility is driven by
  // selectedTab/style, not by mountedTabs identity.
  const mountedTabsRef = React.useRef<Set<'all' | 'requests'>>(
    new Set([selectedTab])
  )
  mountedTabsRef.current.add(selectedTab)
  const mountedTabs = mountedTabsRef.current

  const featureFlags = useFeatureFlagService()

  const handleUploadContactsClick = () => {
    if (featureFlags.enableContactImportWizard) {
      navigate(PATHS.CONTACTS_IMPORT_WIZARD)
    } else {
      setShowImportContactModal(true)
    }
  }

  const handleContactCreated = () => {
    queryClient.invalidateQueries({ queryKey: ['tags', 'contacts'] })
    queryClient.refetchQueries({ queryKey: [CONTACTS_TABLE_KEY] })
  }

  const handleContactUpdated = () => {
    queryClient.refetchQueries({ queryKey: [CONTACTS_TABLE_KEY] })
  }

  const selectedTabTitle =
    selectedTab === 'all' ? 'All Contacts' : 'Contact Requests'

  useDocumentTitle(
    `${selectedTab === 'all' ? 'All' : 'Requested'} Contacts - Contacts - First Bite`
  )

  // Check for success or canceled parameters in URL from Stripe redirect
  React.useEffect(() => {
    if (selectedTab === 'requests') {
      const urlParams = new URLSearchParams(window.location.search)
      const success = urlParams.get('success')
      const canceled = urlParams.get('canceled')

      console.log('Checking URL params:', {
        success,
        canceled,
        search: window.location.search,
      })

      if (success === 'true') {
        console.log('Credit purchase successful, showing success toast')
        toast.success('Credits purchased successfully!')

        // Clear the URL parameters
        if (window.history && window.history.replaceState) {
          window.history.replaceState(
            {},
            document.title,
            window.location.pathname
          )
        }

        // If there was a localStorage callback marker, use it and remove it
        const hasCallback =
          localStorage.getItem('creditPurchaseSuccessCallback') === 'true'
        console.log('Credit purchase callback status:', { hasCallback })

        if (hasCallback) {
          localStorage.removeItem('creditPurchaseSuccessCallback')
          console.log('Refreshing credit information after purchase')
          // Refresh credit information
          queryClient.invalidateQueries({
            queryKey: ['creditBalance'],
          })
        }
      } else if (canceled === 'true') {
        console.log('Credit purchase canceled, showing info toast')
        toast.info('Credit purchase was canceled')

        // Clear the URL parameters
        if (window.history && window.history.replaceState) {
          window.history.replaceState(
            {},
            document.title,
            window.location.pathname
          )
        }
      }
    }
  }, [selectedTab, queryClient])

  return (
    <FBPage
      // Tighter outer padding (matches the Deals page) so the table gets more
      // vertical room on laptop-height viewports; the table card keeps its own
      // border + shadow via FBPageTableContainer below.
      innerPageClassName="!bg-surface-page !p-[8px]"
      containerClassName="!bg-surface-page !shadow-none !px-[16px] !py-[12px]"
    >
      {/* 16px gap below the header block + a fixed min-height so the tabs row
          never shifts vertically between tabs. The credit card (Requests tab,
          ~57px) is a hair taller than the title stack (~56px), so we lock the
          block to the taller of the two on both tabs. */}
      <div className="mb-4 flex min-h-[57px] w-full flex-row items-start justify-between gap-4">
        <FBPageTitleRow>
          <div className="flex flex-col">
            <span className="mb-2 inline-flex items-center gap-1.5 self-start rounded-full bg-neutral-100 px-2.5 py-0.5 text-sm font-medium tracking-tight text-neutral-700">
              <PiAddressBook size={14} />
              Contacts
            </span>
            <FBPageTitle className="!text-fg">{selectedTabTitle}</FBPageTitle>
          </div>

          {selectedTab === 'all' && (
            <div className={'flex flex-row gap-2.5 ml-auto'}>
              {/* DS2026: secondary (Upload) on the left, primary (Add) on the
                  right. Default FbButton height (md, min-h-10) so they match the
                  Buy Credits button on the Requests tab. */}
              <FbButton
                onClick={handleUploadContactsClick}
                variant="secondary"
                className="!border-neutral-300 !text-neutral-800 hover:!bg-neutral-100 hover:!text-neutral-900"
              >
                <PiUploadSimple size={18} /> <span>Upload Contacts</span>
              </FbButton>
              <FbButton
                className="!bg-brand-bright !text-white hover:!bg-brand-active"
                onClick={() => setShowNewContactModal(true)}
              >
                <PiPlus size={16} /> Add Contact
              </FbButton>
            </div>
          )}
        </FBPageTitleRow>

        {selectedTab === 'requests' && (
          <CreditBalanceCard hideBuyButton compact />
        )}

        {showImportContactModal && (
          <ImportContactModal
            show={showImportContactModal}
            handleClose={() => setShowImportContactModal(false)}
            onContactsImported={handleContactCreated}
          />
        )}

        {showNewContactModal && (
          <NewContactModal
            show={showNewContactModal}
            handleClose={() => setShowNewContactModal(false)}
            onContactCreated={handleContactCreated}
          />
        )}
      </div>

      <div className="flex items-end justify-between gap-4 mb-3">
        <UnderlineTabs<'all' | 'requests'>
          tabs={[
            {
              key: 'all',
              title: 'All Contacts',
              count: totalContacts,
            },
            {
              key: 'requests',
              title: 'Contact Requests',
            },
          ]}
          activeKey={selectedTab}
          onChange={(value) => {
            const url = `/contacts/${value}`
            navigate(url)
            store.setLastContactVisited(url)
          }}
          idPrefix="contacts"
          aria-label="Contacts type"
          size="sm"
        />
        {selectedTab === 'requests' && <BuyCreditsButton />}
      </div>

      <TableLinkStyleProvider variant="plain">
        <>
          {mountedTabs.has('all') && (
            <FBPageTableContainer
              className="flex-col overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-[0px_1px_3px_0px_rgba(16,24,40,0.08),0px_1px_2px_-1px_rgba(16,24,40,0.04)]"
              style={{
                display: selectedTab === 'all' ? 'flex' : 'none',
              }}
            >
              {/* Provider must wrap the table so it also covers the Columns
                  button, which DataTable renders through a portal. */}
              <ToolbarDensityProvider value="sm">
                <ContactsTable
                  defaultColumnVisibility={{
                    requested_contact__campaign__name: false,
                  }}
                  tableKey={CONTACTS_TABLE_KEY}
                  setTotalRowsCount={setTotalContacts}
                  handleContactUpdated={handleContactUpdated}
                />
              </ToolbarDensityProvider>
            </FBPageTableContainer>
          )}

          {mountedTabs.has('requests') && (
            <FBPageTableContainer
              className="flex-col overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-[0px_1px_3px_0px_rgba(16,24,40,0.08),0px_1px_2px_-1px_rgba(16,24,40,0.04)]"
              style={{
                display: selectedTab === 'requests' ? 'flex' : 'none',
              }}
            >
              <ToolbarDensityProvider value="sm">
                <ContactRequestsTable
                  tableKey={CONTACT_REQUEST_JOB_TABLE_KEY}
                />
              </ToolbarDensityProvider>
            </FBPageTableContainer>
          )}
        </>
      </TableLinkStyleProvider>
    </FBPage>
  )
}
